import type { MeResponse } from '@dnx/contracts';
import type { Permission } from '@dnx/domain';
import { BrandMark } from '@dnx/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate, useRouterState } from '@tanstack/react-router';
import {
  BookOpenCheck, CalendarDays, ClipboardList, FileText, HeartPulse, Scissors, Wallet, ChevronsUpDown, House, type LucideIcon, Megaphone, MessagesSquare, Settings, Users, Workflow,
} from 'lucide-react';
import { countsQuery } from '../inbox/queries.js';
import { useInboxRealtime } from '../inbox/realtime.js';
import { UrgentBanner, postopAlertsQuery } from '../postop/postop-page.js';
import { api } from '../lib/api.js';
import { initials, roleLabel } from '../lib/labels.js';
import { meQuery } from '../lib/session.js';
import { SideGroup, SideLink, StageShell, UserMenu } from './stage-shell.js';
import { allowedSettingsTabs } from '../settings/layout.js';

interface NavItem {
  label: string;
  Icon: LucideIcon;
  to: '/' | '/conversas' | '/leads' | '/agenda' | '/base-de-conhecimento' | '/configuracoes' | '/automacoes' | '/disparos' | '/pacientes' | '/orcamentos' | '/cobrancas' | '/cirurgias' | '/pos-operatorio';
  badge?: 'waiting' | 'alerts';
  permission?: Permission;
}

/** Navegação filtrada por papel (docs/04 §5.1). */
const NAV: { label: string; items: NavItem[] }[] = [
  { label: 'Atendimento', items: [
    { label: 'Início', Icon: House, to: '/' },
    { label: 'Conversas', Icon: MessagesSquare, permission: 'inbox.conversation.read', to: '/conversas', badge: 'waiting' },
    { label: 'Agenda', Icon: CalendarDays, permission: 'scheduling.appointment.read', to: '/agenda' },
    { label: 'Leads', Icon: Users, permission: 'crm.lead.read', to: '/leads' },
    { label: 'Automações', Icon: Workflow, permission: 'crm.automation.manage', to: '/automacoes' },
    { label: 'Disparos', Icon: Megaphone, permission: 'crm.broadcast.manage', to: '/disparos' },
  ] },
  { label: 'Clínica', items: [
    { label: 'Pacientes', Icon: ClipboardList, permission: 'clinical.record.read', to: '/pacientes' },
    { label: 'Cirurgias', Icon: Scissors, permission: 'clinical.record.read', to: '/cirurgias' },
    { label: 'Pós-operatório', Icon: HeartPulse, permission: 'clinical.record.read', to: '/pos-operatorio', badge: 'alerts' },
    { label: 'Orçamentos', Icon: FileText, permission: 'billing.quote.read', to: '/orcamentos' },
    { label: 'Cobranças', Icon: Wallet, permission: 'billing.payment.read', to: '/cobrancas' },
    { label: 'Base de conhecimento', Icon: BookOpenCheck, permission: 'ai.kb.read', to: '/base-de-conhecimento' },
    { label: 'Configurações', Icon: Settings, to: '/configuracoes' },
  ] },
];
/** O que vem nas próximas fases: aparece como uma linha discreta, sem ocupar a navegação. */
const NEXT = ['Gestão', 'App do paciente'];
/** Telas que usam o palco inteiro (listas e quadros com rolagem própria). */
const FULL_BLEED = ['/conversas', '/leads', '/agenda'];

function OrgSwitcher({ me }: { me: MeResponse }) {
  const qc = useQueryClient();
  const active = me.organizations.find((o) => o.organizationId === me.activeOrganizationId);
  const switchOrg = useMutation({
    mutationFn: (organizationId: string) => api<void>('/auth/active-organization', { method: 'POST', body: { organizationId } }),
    onSuccess: () => qc.invalidateQueries(),
  });
  const multi = me.organizations.length > 1;
  return (
    <label className={`relative flex items-center gap-3 rounded-2xl border border-border bg-surface/70 p-2.5 ${multi ? 'cursor-pointer hover:border-border-strong' : ''}`}>
      <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-fg text-[12px] font-bold text-bg">{initials(active?.tradeName ?? '?')}</span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-semibold text-fg">{active?.tradeName}</span>
        <span className="block truncate text-[10.5px] font-semibold tracking-[0.14em] text-fg-subtle uppercase">
          {active?.roles.map(roleLabel).join(' · ')}
        </span>
      </span>
      {multi && (
        <>
          <ChevronsUpDown className="size-4 text-fg-subtle" aria-hidden />
          <select aria-label="Trocar de clínica" className="absolute inset-0 cursor-pointer opacity-0"
            value={me.activeOrganizationId ?? ''} onChange={(e) => switchOrg.mutate(e.target.value)}>
            {me.organizations.map((o) => <option key={o.organizationId} value={o.organizationId}>{o.tradeName}</option>)}
          </select>
        </>
      )}
    </label>
  );
}

export function AppShell() {
  const { data: me } = useSuspenseQuery(meQuery);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });
  const perms = new Set(me.permissions);
  const canInbox = perms.has('inbox.conversation.read');
  useInboxRealtime(canInbox ? me.activeOrganizationId : null);
  const { data: counts } = useQuery({ ...countsQuery, enabled: canInbox });
  const { data: alerts } = useQuery({ ...postopAlertsQuery(false), enabled: perms.has('clinical.record.read') });
  const openAlerts = alerts?.filter((a) => a.status === 'open').length ?? 0;
  const logout = useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST' }),
    onSettled: () => { qc.clear(); void navigate({ to: '/entrar' }); },
  });
  const active = me.organizations.find((o) => o.organizationId === me.activeOrganizationId);

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="px-1"><BrandMark /></div>
      <div className="mt-6"><OrgSwitcher me={me} /></div>
      <nav aria-label="Principal" className="mt-7 flex-1 overflow-y-auto">
        {NAV.map((group) => {
          const visible = group.items.filter((item) => (item.to === '/configuracoes' ? allowedSettingsTabs(me.permissions).length > 0 : !item.permission || perms.has(item.permission)));
          if (!visible.length) return null;
          return (
            <SideGroup key={group.label} label={group.label}>
              {visible.map((item) => (
                <li key={item.to}>
                  <SideLink to={item.to} exact={item.to === '/'} Icon={item.Icon} label={item.label}
                    badge={item.badge === 'waiting' ? !!counts?.waiting && (
                      <span className="grid h-5 min-w-5 place-items-center rounded-full bg-warning px-1.5 text-[11px] font-bold text-white tabular-nums"
                        aria-label={`${counts.waiting} aguardando`}>{counts.waiting}</span>
                    ) : item.badge === 'alerts' && !!openAlerts && (
                      <span className="grid h-5 min-w-5 place-items-center rounded-full bg-danger px-1.5 text-[11px] font-bold text-white tabular-nums"
                        aria-label={`${openAlerts} alertas abertos`}>{openAlerts}</span>
                    )} />
                </li>
              ))}
            </SideGroup>
          );
        })}
        <p className="mt-7 px-3 text-[12px] leading-relaxed text-fg-subtle">
          <span className="font-semibold tracking-[0.14em] uppercase">Em breve</span><br />{NEXT.join(' · ')}
        </p>
      </nav>
      <p className="mt-4 px-3 text-[11.5px] leading-snug text-fg-subtle">Gestão de ponta a ponta para clínicas de transplante capilar.</p>
    </div>
  );

  const group = NAV.find((g) => g.items.some((i) => (i.to === '/' ? path === '/' : path.startsWith(i.to))));
  const item = group?.items.find((i) => (i.to === '/' ? path === '/' : path.startsWith(i.to)));
  const section = path.startsWith('/perfil') ? { group: 'Conta', page: 'Meu perfil' }
    : group && item ? { group: group.label, page: item.label } : undefined;

  return (
    <StageShell
      sidebar={sidebar}
      banner={<UrgentBanner />}
      section={section}
      fullBleed={FULL_BLEED.some((p) => path === p || path.startsWith(`${p}/`))}
      topbar={<UserMenu name={me.user.name} subtitle={active?.roles.map(roleLabel).join(' · ') ?? me.user.email}
        profileTo="/perfil" onLogout={() => logout.mutate()} />}
    />
  );
}
